import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { AxiosResponse, HttpStatusCode } from "axios";

import { EMAIL_REGEX } from "../../consts/regex";

import {
  checkAuthCodeByEmail,
  checkEmailDuplicate,
  sendAuthCodeByEmail,
} from "../../services/authServices";

import useSimplePopup from "../useSimplePopup";
import useFullScreenLoading from "../useFullScreenLoading";

import SimplePopup from "../../components/Popup/Simple";
import CommonInput from "../../components/Input/CommonInput";
import CommonButton from "../../components/Button";
import CommonPopup from "../../components/Popup";

import { FindIdInfoIcon } from "../../pages/Auth/FindId";
import emailDupConfirm from "../../assets/img/email-dup-confirm.png";

type Props = {
  label?: string;
  sendEmail?: (params: any) => Promise<AxiosResponse<any>>;
  extraParams?: any;
  onSuccess?: () => void;
  useFindPassword?: boolean;
};

export type EmailVerification = {
  email: string;
  duplicateCheckedEmail?: string;
  duplicateChecked: boolean;
  authRequested: boolean;
  authCode: string;
};

export default function useEmailVerification({
  onSuccess,
  extraParams = {},
  label = "아이디(이메일)",
  sendEmail = sendAuthCodeByEmail,
  useFindPassword = true,
}: Props): [
  { data: EmailVerification; isComplete: boolean; validate: () => boolean },
  React.ReactNode
] {
  const navigate = useNavigate();
  const [data, setData] = useState<EmailVerification>({
    email: "",
    duplicateChecked: false,
    authRequested: false,
    authCode: "",
  });
  const [popup, setPopup] = useState<
    | "emailDuplicated"
    | "emailNotDuplicated"
    | "emailSend"
    | "emailComplete"
    | ""
  >("");
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const hostList = [
    "gmail.com",
    "naver.com",
    "kakao.com",
    "apple.com",
    "nate.com",
    "hanmail.net",
    "daum.net",
  ];
  const [autoCompleteList, setAutoCompleteList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [emailError, setEmailError] = useState("");
  const [authCodeError, setAuthCodeError] = useState<string>("");

  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const emailCertInputRef = React.useRef<HTMLInputElement>(null);
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const emailValidate = (dryrun: boolean = false) => {
    if (!data.email?.length || data.email?.length <= 0) {
      if (dryrun === false) {
        setEmailError("이메일을 입력해주세요.");
        emailInputRef.current?.focus();
      }
      return false;
    }
    if (!EMAIL_REGEX.test(data.email as string)) {
      if (dryrun === false) {
        setEmailError("이메일 형식이 올바르지 않습니다.");
        emailInputRef.current?.focus();
      }
      return false;
    }

    return true;
  };

  const validate = () => {
    if (!emailValidate()) return false;

    if (!data.duplicateChecked) {
      setEmailError("이메일 중복 확인을 해주세요.");
      emailInputRef.current?.focus();
      return false;
    }
    if (data.authRequested && !data.authCode?.length) {
      setAuthCodeError("인증번호를 입력해주세요.");
      emailCertInputRef.current?.focus();
      return false;
    }

    if (!isComplete) {
      setAuthCodeError("이메일 인증을 완료해주세요.");
      emailCertInputRef.current?.focus();
      return false;
    }

    return true;
  };

  useEffect(() => {
    setData((prev) => ({ ...prev, authCode: "" }));
  }, [data.authRequested]);

  return [
    { data, isComplete, validate },
    <div>
      {loadingComponent}
      <div className="py-2.5 text-sm text-brand-neutral-60">{label}</div>
      <div className="relative">
        <CommonInput
          type="email"
          placeholder="이메일을 입력해주세요"
          value={data.email ?? ""}
          onChange={(text) => {
            setData((prev) => ({
              ...prev,
              email: text,
              duplicateChecked: false,
              authRequested: false,
            }));
            if (text.includes("@")) {
              const tempList = hostList.map(
                (host) => `${text.split("@")[0]}@${host}`
              );
              setAutoCompleteList(
                tempList.filter((item) => item.includes(text))
              );
            } else {
              setAutoCompleteList([]);
            }
            setEmailError("");
            setIsComplete(false);
          }}
          className="focus:border-brand-primary-magenta"
          error={emailError}
          ref={emailInputRef}
          postfix={{
            content: (
              <>
                {(!data.duplicateChecked ||
                  data.email !== data.duplicateCheckedEmail) && (
                  <CommonButton
                    variant={emailValidate(true) ? "primary" : "tertiary-s"}
                    className="absolute right-1 top-1/2 -translate-y-1/2 whitespace-nowrap text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px]"
                    onClick={() => {
                      if (!emailValidate()) return;

                      setIsLoading(true);
                      setEmailError("");
                      checkEmailDuplicate({
                        email: data.email.trim() as string,
                      })
                        .then((res) => {
                          if (res.data.isDuplicated === false) {
                            setPopup("emailNotDuplicated");
                            setData((prev) => ({
                              ...prev,
                              duplicateChecked: true,
                              duplicateCheckedEmail: data.email,
                            }));
                            setIsComplete(false);
                          } else {
                            setPopup("emailDuplicated");
                          }
                        })
                        .catch((reason) => {
                          setPopup("emailDuplicated");
                        })
                        .finally(() => setIsLoading(false));
                    }}
                  >
                    중복 확인
                  </CommonButton>
                )}
                {data.duplicateChecked &&
                  data.email === data.duplicateCheckedEmail && (
                    <CommonButton
                      variant="tertiary-s"
                      className="absolute right-1 top-1/2 -translate-y-1/2 whitespace-nowrap"
                      onClick={() => {
                        if (data.authRequested === true) {
                          setIsComplete(false);
                          setData((prev) => ({
                            ...prev,
                            authCode: "",
                          }));
                        }

                        if (!emailValidate()) return;

                        setIsLoading(true);
                        sendEmail({
                          email: data.email.trim() as string,
                          ...extraParams,
                        })
                          .then((res) => {
                            setPopup("emailSend");
                            if (res.status === HttpStatusCode.Ok) {
                              setData((prev) => ({
                                ...prev,
                                authRequested: true,
                              }));
                              onSuccess?.();
                            }
                          })
                          .catch((reason) => {
                            setPopup("emailDuplicated");
                          })
                          .finally(() => setIsLoading(false));
                      }}
                    >
                      {data.authRequested ? "다시 보내기" : "이메일 인증"}
                    </CommonButton>
                  )}
              </>
            ),
          }}
        />
        {autoCompleteList.filter((item) => item !== data.email).length > 0 && (
          <div className="absolute top-[54px] py-[5px] z-20 bg-white border border-brand-primary-magenta w-full">
            {autoCompleteList
              .filter((item) => item !== data.email)
              .map((item, index) => {
                const [before, after] = item.split(data.email);
                return (
                  <div
                    key={index}
                    className="px-4 h-[40px] flex items-center"
                    onClick={() => {
                      setData((prev) => ({ ...prev, email: item }));
                      setAutoCompleteList([]);
                    }}
                  >
                    {before}
                    <strong>{data.email}</strong>
                    {after}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {data.authRequested && (
        <div className="flex flex-col">
          <div className="py-2.5 text-sm text-brand-neutral-60">인증번호</div>
          <div className={clsx(["relative", { "opacity-60": isComplete }])}>
            <CommonInput
              placeholder="인증번호 입력"
              value={data.authCode}
              onChange={(text) => {
                setAuthCodeError("");
                setData((prev) => ({ ...prev, authCode: text.slice(0, 6) }));
                setIsComplete(false);
              }}
              disabled={isComplete}
              error={authCodeError}
              type="number"
              postfix={{
                content: (
                  <CommonButton
                    variant={
                      data?.authCode?.length === 6 ? "primary" : "tertiary-s"
                    }
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-[70px] whitespace-nowrap text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px]"
                    onClick={() => {
                      if (
                        !data?.authCode?.length ||
                        data?.authCode?.length <= 0
                      ) {
                        open("인증번호를 입력해주세요.");
                        return;
                      }

                      setIsLoading(true);
                      checkAuthCodeByEmail({
                        authCode: data.authCode,
                        email: data.email.trim(),
                      })
                        .then((res) => {
                          if (res.status === HttpStatusCode.Ok) {
                            setPopup("emailComplete");
                            setAuthCodeError("");
                            setIsComplete(true);
                          } else {
                            setAuthCodeError("잘못된 인증번호입니다.");
                          }
                        })
                        .catch((reason) => {
                          setAuthCodeError("잘못된 인증번호입니다.");
                        })
                        .finally(() => setIsLoading(false));
                    }}
                  >
                    {isComplete ? "인증 완료" : "인증 확인"}
                  </CommonButton>
                ),
              }}
              pattern="[0-9]*"
              ref={emailCertInputRef}
            />
          </div>
        </div>
      )}

      <SimplePopup
        isOpen={popup === "emailSend"}
        onClose={() => setPopup("")}
        message="인증 이메일이 발송되었습니다."
      />

      <SimplePopup
        isOpen={popup === "emailComplete"}
        onClose={() => setPopup("")}
        message="확인되었습니다."
      />

      <CommonPopup
        title="이메일 중복"
        unUseHorizonRule
        padding={0}
        isOpen={popup === "emailDuplicated"}
      >
        <div className="px-5 pb-5">
          <div className="py-2.5 flex flex-col gap-2.5 items-center">
            <FindIdInfoIcon />
            <div className=" whitespace-pre-line text-center text-brand-neutral-60 text-sm">
              이전에 가입하신 이메일이에요.
            </div>
          </div>
        </div>

        <div className="w-full flex">
          {useFindPassword && (
            <CommonButton
              variant="secondary"
              className="h-bottom-button w-full rounded-none"
              onClick={() => {
                setPopup("");
                navigate("/auth/findPassword");
              }}
            >
              비밀번호 찾기
            </CommonButton>
          )}
          <CommonButton
            className="h-bottom-button w-full rounded-none"
            onClick={() => setPopup("")}
          >
            다른 이메일로 할게요
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup
        title="이메일 확인 완료"
        unUseHorizonRule
        padding={0}
        isOpen={popup === "emailNotDuplicated"}
      >
        <div className="px-5 pb-5">
          <div className="py-2.5 flex flex-col gap-2.5 items-center">
            <img src={emailDupConfirm} alt="dup-confirm" className="h-[60px]" />
            <div className=" whitespace-pre-line text-center text-brand-neutral-60 text-sm">
              {"사용하실 수 있는 이메일이에요.\n이메일 인증을 진행해주세요."}
            </div>
          </div>
        </div>

        <div className="w-full flex">
          <CommonButton
            className="h-bottom-button w-full rounded-none"
            onClick={() => {
              setIsLoading(true);
              sendEmail({ email: data.email.trim() as string, ...extraParams })
                .then((res) => {
                  setPopup("emailSend");
                  setData((prev) => ({ ...prev, authRequested: true }));
                  onSuccess?.();
                })
                .catch((reason) => {
                  setPopup("emailDuplicated");
                })
                .finally(() => setIsLoading(false));
            }}
          >
            이메일 인증하기
          </CommonButton>
        </div>
      </CommonPopup>

      {simplePopupComponent}
    </div>,
  ];
}
